import React from "react";
import styled from "styled-components";
import { AccentBar, SectionHeading } from "./SessionPageComponents";
import { PrimaryButtonLink } from "./Buttons";

const APPLY_URL =
  "https://blocksurvey.io/the-submission-amsterdam-71v0jGRiSEGFXl3abOxRVA?v=o";

const TIMING_CARDS = [
  {
    numeral: "I",
    title: "The Encounter",
    arc: "One full arc",
    arcDescription: "1.5 – 3 hours",
    description:
      "A single, complete play date. We settle in, the dynamic begins, and something shifts. I take you somewhere you couldn't reach alone. And when we land, I bring you back. We unwind togehter. Nothing lingers more than it feels right. Nothing is cut short.",
  },
  {
    numeral: "II",
    title: "The Indulgence",
    arc: "A full afternoon or an evening",
    arcDescription: "4 - 6 hours",
    description:
      "More time. More texture. We play when the dynamic calls for it. We go for drinks or dinner when it doesn't. No fixed rounds, no announced transitions. The experience dictates the rhythm.",
  },
  {
    numeral: "III",
    title: "Mine for a Day",
    arc: "Brunch to bedtime",
    arcDescription: "10 - 12 hours",
    description:
      "A full day together — inside the dynamic and outside it. The deepest immersion available in a single encounter. What happens is between us. How long it lasts is dictated by the day itself.",
  },
];

export const SessionTimingSection = () => (
  <SectionWrapper>
    <SectionHeader>
      <Eyebrow>Time is not the currency here</Eyebrow>
      <SectionHeading>The Clock Is Not Invited</SectionHeading>
      <AccentBar />
      <IntroParagraph>
        Every experience I offer has a natural arc: a beginning, a middle, and
        an end that arrives when it should. You are here to be taken somewhere.
        Time is secondary.
      </IntroParagraph>
    </SectionHeader>

    <CardsGrid>
      {TIMING_CARDS.map((card) => (
        <TimingCard key={card.numeral} {...card} />
      ))}
    </CardsGrid>
  </SectionWrapper>
);

const TimingCard = ({ numeral, title, arc, arcDescription, description }) => (
  <Card>
    <CardHeader>
      <CardNumeral>{numeral}</CardNumeral>
      <CardTitle>{title}</CardTitle>
      <CardAccentBar />
      <CardArcLabel>{arc}</CardArcLabel>
      <CardDuration>{arcDescription}</CardDuration>
    </CardHeader>

    <CardBody>
      <CardDescription>{description}</CardDescription>
      <PrimaryButtonLink href={APPLY_URL}>Apply to Serve</PrimaryButtonLink>
    </CardBody>
  </Card>
);

/* ─── Section wrapper ─── */

const SectionWrapper = styled.section`
  margin: 8rem 0;
`;

/* ─── Section header ─── */

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Eyebrow = styled.p`
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--color-emphasis);
  margin: 0 0 1.25rem;
`;

const IntroParagraph = styled.p`
  /* max-width: 690px; */
  margin: 0 auto;
  padding: 0 2rem;
  font-size: 18px;
  line-height: 1.8;
  color: var(--color-bone);

  @media screen and (max-width: 600px) {
    padding: 0;
  }
`;

/* ─── Cards grid ─── */

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid color-mix(in srgb, var(--color-bone) 10%, transparent);

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ─── Individual card ─── */

const Card = styled.article`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-right: 1px solid color-mix(in srgb, var(--color-bone) 10%, transparent);

  &:last-child {
    border-right: none;
  }

  /* Tablet: 2-column internal layout */
  @media screen and (max-width: 768px) {
    flex-direction: row;
    gap: 3rem;
    border-right: none;
    border-bottom: 1px solid
      color-mix(in srgb, var(--color-bone) 20%, transparent);
    padding: 2rem 0;

    &:last-child {
      border-bottom: none;
    }
  }

  /* Mobile: back to vertical stack */
  @media screen and (max-width: 500px) {
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.75rem 0;
  }
`;

const CardHeader = styled.div`
  /* Tablet: fixed-width left column */
  @media screen and (max-width: 768px) and (min-width: 501px) {
    width: 40%;
    flex-shrink: 0;
  }
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

/* ─── Card content ─── */

const CardNumeral = styled.span`
  display: block;
  font-size: 11px;
  letter-spacing: 0.15em;
  color: var(--color-bone);
  margin-bottom: 0.75rem;
`;

const CardTitle = styled.h3`
  font-family: "Ysabeau";
  font-weight: 300;
  font-size: 1.4rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--color-bone);
  margin: 0 0 0.4rem;
`;

const CardAccentBar = styled(AccentBar)`
  margin: 0.5rem 0 1rem;
`;

const CardArcLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-bone);
  margin: 0 0 0.5rem;
`;

const CardDuration = styled.p`
  font-style: italic;
  font-size: 14px;
  color: var(--color-bone);
  margin: 0;
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  font-size: 14px;
  line-height: 1.85;
  color: var(--color-bone);
  margin: 0 0 2rem;
  flex: 1;

  @media screen and (max-width: 500px) {
    margin-top: 0.75rem;
    font-size: 17px;
  }
`;
